"use client";
import { BriefcaseBusiness, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { faqs } from '@/lib/content';
import Link from 'next/link';

const FAQSection = () => {
  // Generate JSON-LD for SEO Rich Snippets
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className='min-h-screen w-full py-24 px-6 lg:px-12 bg-white' aria-labelledby="faq-title">
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className='max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start'>

        {/* Left Column: Context & Action */}
        <div className='flex flex-col gap-10 lg:sticky lg:top-24'>
          <header className="flex flex-col w-full">
            <div className="flex flex-col gap-4">
              <span className="flex items-center gap-2 font-bold text-[#4F7AC2] tracking-widest uppercase text-xs">
                <BriefcaseBusiness className="w-4 h-4" aria-hidden="true" />
                Support & Knowledge
              </span>
              <h2 id="faq-title" className='text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] tracking-tight'>
                Find the Answers <br /> You Need
              </h2>
            </div>
            <p className="mt-6 max-w-xl text-lg text-gray-600 leading-relaxed font-medium">
              From strategic planning to sustainable growth, our tailored solutions help businesses and individuals achieve clarity, security, and long-term success.
            </p>
          </header>

          <div className='flex items-center gap-4'>
            <Link href="/contact" className='flex items-center h-14 px-12 bg-[#4F7AC2] text-white rounded-xl font-bold hover:bg-[#3d5f99] transition-all shadow-lg'>
              Get Started
              <ArrowRight className='ml-2 w-4 h-4' aria-hidden="true" />
            </Link>
          </div>

          <div className='relative h-[400px] rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100'>
            <Image
              src='/images/faq.jpg'
              alt='Professional consultants helping a client'
              fill
              className='object-cover'
              sizes="(max-width: 1024px) 100vw, 50vw"
              priority={false}
            />
          </div>
        </div>

        {/* Right Column: Interactive Accordion */}
        <div 
          className='w-full' 
          role="region" 
          aria-label="Frequently Asked Questions list"
        >
          <Accordion
            type="single"
            collapsible
            className="w-full space-y-4"
          >
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-gray-50 border-none rounded-2xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <AccordionTrigger
                  className="px-8 text-gray-900 data-[state=open]:bg-[#4F7AC2] data-[state=open]:text-white text-lg hover:no-underline text-left font-bold py-7 transition-all duration-500 ease-in-out"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="p-0">
                  <div className="bg-white m-3 mt-0 p-8 rounded-xl text-gray-600 text-lg leading-relaxed shadow-inner">
                    {faq.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

      </div>
    </section>
  )
}

export default FAQSection;